import React from "react";
import { Trash2 } from "lucide-react";

import styles from "./editPageHeader.module.scss";

/**
 * The header every section editor opens with — Picks, Summary sections and the
 * Featured Summary card — so the three read as one family:
 *
 *   Edit Picks Section                       [actions]
 *   Self-Made Success Stories 🔒
 *   note (optional)
 *
 * `title` says what kind of screen this is; `subtitle` names the section being
 * edited (its English title). `badge` sits beside the subtitle (the lock on a
 * personalised rail), `note` explains anything unusual, `actions` go right.
 */
const EditPageHeader = ({ title, subtitle, badge, note, actions }) => (
    <div className={styles.header}>
        <div className={styles.text}>
            <h4 className={styles.title}>{title}</h4>
            {(subtitle || badge) && (
                <div className={styles.subtitle}>
                    {subtitle}
                    {badge}
                </div>
            )}
            {note && <p className={styles.note}>{note}</p>}
        </div>
        {actions && <div className={styles.actions}>{actions}</div>}
    </div>
);

/**
 * A two-or-more-way switch for the header's right side — Automatic /
 * Hand-picked on every section editor. `options` are `{ value, label, title?,
 * disabled? }`.
 */
export const SegmentedToggle = ({ label, options, value, onChange }) => (
    <div className={styles.segmentedWrap}>
        {label && <span className={styles.segmentedLabel}>{label}</span>}
        <div className={styles.segmented} role="group" aria-label={label}>
            {options.map((opt) => (
                <button
                    key={opt.value}
                    type="button"
                    className={`${styles.segment} ${value === opt.value ? styles.segmentActive : ""}`}
                    aria-pressed={value === opt.value}
                    title={opt.title}
                    disabled={opt.disabled}
                    onClick={() => value !== opt.value && onChange(opt.value)}
                >
                    {opt.label}
                </button>
            ))}
        </div>
    </div>
);

/**
 * Removes the section being edited. Lives in the header's actions, top right,
 * rather than on every list row: deleting is a decision about one section, made
 * while looking at it, not something to be one stray click away in a table.
 */
export const HeaderDeleteButton = ({ onClick, disabled, label = "Delete" }) => (
    <button type="button" className={`btn ${styles.deleteButton}`} onClick={onClick} disabled={disabled}>
        <Trash2 size={16} />
        {label}
    </button>
);

/** The page around it: the same gutter on every section editor. */
export const EditPageLayout = ({ children }) => <div className={styles.page}>{children}</div>;

export default EditPageHeader;
